import type { CarrierContactViewModel } from '../../../../api/types/CarrierContactViewModel';
import { formatPhoneNumber } from '../../../../utils/format-phone';

export type CarrierContactRecord = {
  id: string;
  role: string;
  contactName: string;
  email: string;
  phone: string;
  isPam: boolean;
};

export const CARRIER_CONTACTS_RENDERED_ROW_THRESHOLD = 8;

export function isValidCarrierContactViewModel(
  value: CarrierContactViewModel | null | undefined
): value is CarrierContactViewModel {
  return Boolean(
    value?.id &&
    value.role &&
    value.contactName &&
    typeof value.isPam === 'boolean'
  );
}

export function normalizeCarrierContactRecords(
  records: Array<CarrierContactViewModel> | null | undefined
): CarrierContactRecord[] {
  return (records ?? []).filter(isValidCarrierContactViewModel).map((record) => ({
    id: record.id!,
    role: record.role!,
    contactName: record.contactName!,
    email: record.email!,
    phone: record.phone ? formatPhoneNumber(record.phone) : '',
    isPam: record.isPam!,
  }));
}

function compareAlphabetically(left: string, right: string): number {
  return left.localeCompare(right, 'en', { sensitivity: 'base' });
}

export function sortCarrierContacts(records: CarrierContactRecord[]): CarrierContactRecord[] {
  return [...records].sort((left, right) => {
    const byRole = compareAlphabetically(left.role, right.role);

    if (byRole !== 0) {
      return byRole;
    }

    return compareAlphabetically(left.contactName, right.contactName);
  });
}

export function getPamContact(records: CarrierContactRecord[]): CarrierContactRecord | null {
  return records.find((record) => record.isPam) ?? null;
}

export function getRenderedRowCount(records: CarrierContactRecord[]): number {
  const roleCount = new Set(records.map((record) => record.role)).size;

  return records.length + roleCount;
}
